import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { fetchAllSportsNews, getSupportedSports } from '../services/newsService';
import { SportIcon } from '../utils/sportIcons';

const SportsNews = () => {
  const [allNews, setAllNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const supportedSports = getSupportedSports();

  useEffect(() => {
    loadAllSportsNews();
  }, []);

  const loadAllSportsNews = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all sports news in one efficient API call
      const allNewsData = await fetchAllSportsNews(3);
      setAllNews(allNewsData);
    } catch (err) {
      setError('Failed to load news. Please try again later.');
      console.error('News loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Sports News Header */}
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: 600,
          color: 'primary.main'
        }}
      >
        Sports News
      </Typography>

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Horizontal Scrolling News Feed */}
      {!loading && !error && allNews.length > 0 && (
        <Box
          sx={{
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              pb: 1,
              '&::-webkit-scrollbar': {
                height: 4
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: 2
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'primary.main',
                borderRadius: 2,
                '&:hover': {
                  background: 'primary.dark'
                }
              }
            }}
          >
            {allNews.map((article, index) => (
              <Card 
                key={article.guid || index} 
                elevation={2}
                onClick={() => window.open(article.link, '_blank', 'noopener,noreferrer')}
                sx={{ 
                  minWidth: { xs: 280, sm: 320 },
                  maxWidth: { xs: 280, sm: 320 },
                  flexShrink: 0,
                  cursor: 'pointer',
                  '&:hover': { 
                    elevation: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 2, minHeight: 120, display: 'flex', flexDirection: 'column' }}>
                  {/* Sport Badge */}
                  <Box sx={{ mb: 1 }}>
                    <Chip
                      icon={<SportIcon sportTitle={article.sport} fontSize="small" />}
                      label={article.sport}
                      size="small"
                      color="primary"
                      variant="contained"
                      sx={{ 
                        fontSize: '0.7rem',
                        height: 20,
                        fontWeight: 600
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        wordWrap: 'break-word',
                        hyphens: 'auto'
                      }}
                    >
                      {article.title}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* No News Message */}
      {!loading && !error && allNews.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            No sports news available at the moment.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SportsNews;
